/**
 * Alle sichtbaren Texte an einem Ort. In JSX steht kein hartkodierter Text -
 * so bleibt die Sprache konsistent und spaeter uebersetzbar.
 */
export const de = {
  app: {
    name: 'Ole-Privat',
    tagline: 'Dein Leben auf einen Blick',
  },
  nav: {
    dashboard: 'Übersicht',
    calendar: 'Kalender',
    tasks: 'Aufgaben',
    habits: 'Gewohnheiten',
    goals: 'Ziele',
    projects: 'Projekte',
    lifeWheel: 'Lebensrad',
    review: 'Rückblick',
    journal: 'Journal',
    /** F-48: Die Seite heisst sichtbar „Notizen & Gedanken“ - die Adresse bleibt `#/ressourcen`. */
    resources: 'Notizen & Gedanken',
    settings: 'Einstellungen',
  },
  areas: {
    title: 'Bereiche',
    /** F-48: ein Begriff - „Bereich“, nicht „Lebensbereich“. */
    emptyShort: 'Noch keine Bereiche.',
    /** Beschriftung des Auswahlfelds - man waehlt genau einen. */
    field: 'Bereich',
    subtitle: 'Bereiche ordnen alles: Termine, Aufgaben, Ziele und Gewohnheiten.',
    none: 'Ohne Bereich',
    add: 'Bereich hinzufügen',
    edit: 'Bereich bearbeiten',
    name: 'Name',
    namePlaceholder: 'z. B. Gesundheit',
    color: 'Farbe',
    empty: 'Noch keine Bereiche angelegt.',
    archived: 'Archiviert',
    archive: 'Archivieren',
    unarchive: 'Wiederherstellen',
    showArchived: 'Archivierte anzeigen',
    deleteTitle: 'Bereich löschen?',
    deleteBody:
      'Der Bereich kommt für 30 Tage in den Papierkorb. Termine, Aufgaben und Gewohnheiten bleiben erhalten und werden auf „Ohne Bereich" gesetzt.',
    lifeWheel: 'Achse im Lebensrad',
    lifeWheelHint: 'Bereiche ohne Achse zählen weiterhin überall mit, tauchen aber nicht in der Bewertung auf.',
    filterAll: 'Alle Bereiche',
    filterHint: 'Bereiche filtern',
  },
  settings: {
    title: 'Einstellungen',
    appearance: 'Darstellung',
    theme: 'Erscheinungsbild',
    themeLight: 'Hell',
    themeDark: 'Dunkel',
    themeSystem: 'System',
    data: 'Daten & Sicherung',
    storage: 'Speicher',
    storagePersistent: 'Dauerhaft — der Browser löscht deine Daten nicht von selbst.',
    storageBestEffort: 'Nicht dauerhaft — der Browser darf deine Daten bei Platzmangel löschen.',
    storageMakePersistent: 'Speicher dauerhaft machen',
    storageUsage: 'Belegt',
    storageUnknown: 'Nicht verfügbar in diesem Browser.',
    storageHowTo: 'So wird er dauerhaft: die App installieren (Chrome-Menü → „App installieren“), sie regelmäßig nutzen und im Browser als Lesezeichen speichern. Chrome entscheidet selbst – bis dahin schützen dich Synchronisierung und regelmäßige Sicherungen.',
  },
  weather: {
    title: 'Wetter',
    explain:
      'Zeigt die Vorhersage im Kalenderkopf. Dafür werden Koordinaten und Zeitraum an Open-Meteo gesendet — sonst nichts. Der Dienst braucht keine Anmeldung. Standardmäßig ist die Anzeige aus.',
    enable: 'Wetter im Kalender anzeigen',
    detect: 'Standort ermitteln',
    detecting: 'Wird ermittelt …',
    detected: (lat: number, lon: number) =>
      `Standort übernommen: ${lat.toFixed(2)}, ${lon.toFixed(2)}.`,
    detectFailed: 'Standort konnte nicht ermittelt werden. Berlin bleibt eingestellt.',
  },
  update: {
    hinweis: 'Neue Version verfügbar.',
    neuLaden: 'Neu laden',
    spaeter: 'Später',
  },
  backup: {
    title: 'Sicherung',
    explain:
      'Deine Daten liegen in diesem Browser – mit Synchronisierung zusätzlich verschlüsselt auf dem Server. Lade trotzdem regelmäßig eine Sicherungsdatei herunter: Sie ist die einzige Kopie, die du ohne Passwort und ohne Internet wieder einspielen kannst.',
    export: 'Sicherung herunterladen',
    exporting: 'Wird erstellt …',
    import: 'Sicherung einspielen',
    lastExport: 'Letzte Sicherung',
    never: 'noch nie',
    nagTitle: 'Zeit für eine Sicherung',
    nagBody: (days: number) => (days < 0
      ? 'Deine letzte Sicherung trägt ein Datum in der Zukunft – sichere am besten neu. Eine Sicherung dauert einen Klick.'
      : `Deine letzte Sicherung ist ${days} Tage her. Eine Sicherung dauert einen Klick.`),
    nagNever: 'Du hast noch nie eine Sicherung heruntergeladen.',
    nagDismiss: 'Später',
    importTitle: 'Sicherung einspielen?',
    importBody: (incoming: number, current: number) =>
      `Die Datei enthält ${incoming} Einträge. Dein aktueller Stand mit ${current} Einträgen wird dabei vollständig ersetzt.`,
    importSafetyNote:
      'Von deinem aktuellen Stand wird vorher automatisch eine Sicherung heruntergeladen.',
    /** F-45: Der Papierkorb steht nicht in der Sicherung - das Einspielen leert ihn. */
    importPapierkorb: (n: number) =>
      `Der Papierkorb (${n === 1 ? '1 Eintrag' : `${n} Einträge`}) wird dabei geleert und ist nicht in der Sicherheitskopie.`,
    importConfirm: 'Ersetzen',
    importSuccess: 'Sicherung eingespielt.',
    exportSuccess: 'Sicherung heruntergeladen.',
    errorNotOurFile: 'Das ist keine Sicherungsdatei von Ole-Privat.',
    errorTooNew: 'Diese Sicherung stammt aus einer neueren Version der App.',
    errorNoMigration: (v: number) => `Für Schema-Version ${v} gibt es keinen Migrationspfad.`,
    errorCountMismatch: 'Die Datei ist beschädigt: Die Einträge passen nicht zur Prüfsumme.',
    errorInvalid: 'Die Datei konnte nicht gelesen werden.',
    // F-46: automatische Sicherung in einen Ordner
    auto: {
      title: 'Automatisch sichern',
      explain: 'Einmal pro Woche legt die App eine Sicherungsdatei in einen Ordner deiner Wahl. Das klappt nur, solange die App offen ist.',
      hint: 'Tipp: Nimm einen eigenen Unterordner. Liegt er in Google Drive, hast du eine Kopie außerhalb des Geräts – die Datei ist nicht verschlüsselt und liegt dann lesbar bei Google.',
      unsupported: 'Geht nur in Chrome am Computer oder Chromebook. Hier bleibt es bei Sync und Sicherung von Hand.',
      choose: 'Ordner wählen',
      change: 'Ordner ändern',
      off: 'Ausschalten',
      now: 'Jetzt sichern',
      saving: 'Wird gespeichert …',
      folder: 'Ordner',
      last: 'Zuletzt automatisch',
      next: (d: string) => `nächste ab ${d}`,
      keep: 'Behalten',
      keepOption: (n: number) => `die letzten ${n}`,
      saved: (datei: string) => `Gespeichert: ${datei}`,
      chooseFailed: 'Dieser Ordner geht nicht. Nimm am besten einen eigenen Unterordner.',
      needsPermission: (o: string) => `Chrome braucht deine Freigabe für den Ordner „${o}“.`,
      permissionTip: 'Wählst du „Bei jedem Besuch zulassen“, fragt Chrome seltener.',
      grant: 'Freigeben',
      denied: (o: string) => `Kein Zugriff auf „${o}“. Wähle den Ordner neu.`,
      notFound: (o: string) => `Ordner „${o}“ nicht gefunden. Verschoben oder gelöscht?`,
      full: 'Im Ordner ist kein Platz mehr.',
      writeFailed: 'Die Sicherung konnte nicht gespeichert werden.',
      protectedHint: 'Deutlich weniger Einträge als beim letzten guten Stand – alte Sicherungen bleiben vorerst erhalten.',
      protectedOk: 'Neuer Stand stimmt',
      protectedOkHint: 'Hast du bewusst viel gelöscht? Dann bestätige den neuen Stand – ab der nächsten Sicherung wird wieder aufgeräumt.',
      settingFailed: 'Das hat nicht geklappt. Versuch es bitte noch einmal.',
      bannerPermission: 'Die automatische Sicherung wartet auf deine Freigabe.',
      filesTitle: (n: number) => `Im Ordner (${n})`,
      filesTotal: (g: string) => `zusammen ${g}`,
      filesEmpty: 'Noch keine automatische Sicherung im Ordner.',
      colDate: 'Gesichert am',
      colSize: 'Größe',
      lastKind: ' (automatisch)',
    },
  },
  goals: {
    subtitle: 'Jahresziele geben die Richtung, die kurzen Zyklen bringen dich hin.',
    add: 'Ziel',
    edit: 'Ziel bearbeiten',
    create: 'Neues Ziel',
    empty: 'Noch keine Ziele. Fang mit einem Jahresziel an und häng einen kurzen Zyklus darunter.',
    why: 'Warum',
    whyPlaceholder: 'Wozu ist dir das wichtig?',
    titlePlaceholder: 'z. B. Fit werden',
    horizon: 'Zeitraum',
    parent: 'Zahlt ein auf',
    noParent: 'Eigenständig',
    period: 'Läuft von … bis',
    keyResults: 'Key Results',
    keyResultHint: 'Was ist am Ende messbar anders? Als Zahl, Anzahl, Prozent oder Ja/Nein.',
    krTitle: 'Bezeichnung',
    krTitlePlaceholder: 'z. B. Bücher gelesen, Notenschnitt, Ausgaben pro Monat',
    krUnit: 'Einheit',
    krUnitPlaceholder: 'z. B. €, Seiten',
    krStart: 'Start',
    krTarget: 'Ziel',
    krCurrent: 'Ist',
    addKeyResult: 'Key Result hinzufügen',
    noKeyResults: 'Ohne Key Result bleibt das ein Vorsatz, kein Ziel.',
    remaining: (tage: number) => (tage === 0 ? 'Zeitraum abgelaufen' : `noch ${tage} Tage`),
    deleteTitle: 'Ziel löschen?',
    deleteBody:
      'Das Ziel kommt für 30 Tage in den Papierkorb. Untergeordnete Ziele bleiben erhalten und stehen danach eigenständig da.',
    markReached: 'Erreicht',
    markDropped: 'Verworfen',
    reopen: 'Wieder aufnehmen',
    /** Stabile Schluessel - der Text darf sich aendern, ohne Antworten zu verlieren. */
    horizons: {
      jahr: 'Jahr',
      quartal: 'Quartal',
      '12wochen': '12 Wochen',
    },
    horizonSections: {
      jahr: 'Jahresziele',
      quartal: 'Quartalsziele',
      '12wochen': '12-Wochen-Ziele',
    },
    status: {
      offen: 'Offen',
      laeuft: 'Läuft',
      erreicht: 'Erreicht',
      verworfen: 'Verworfen',
    },
    pace: {
      voraus: 'voraus',
      im_plan: 'im Plan',
      hinten: 'hinten dran',
    },
    paceHint: (ziel: number, zeit: number) =>
      `${ziel} % geschafft, ${zeit} % der Zeit verstrichen`,
  },

  lifeWheel: {
    subtitle: 'Wie voll fühlt sich jeder Bereich gerade an? Nicht die Zahl zählt, sondern die Bewegung.',
    noAxes:
      'Keine Bereiche fürs Lebensrad freigegeben. Unter „Bereiche" lässt sich je Bereich einstellen, ob er eine Achse bekommt.',
    save: 'Bewertung speichern',
    update: 'Bewertung aktualisieren',
    saved: 'Gespeichert',
    average: 'Durchschnitt',
    note: 'Notiz',
    notePlaceholder: 'Was fällt dir auf?',
    history: 'Frühere Messungen',
    compare: 'Vergleich mit',
  },

  journal: {
    subtitle: 'Zwei Minuten morgens, zwei abends. Mehr braucht es nicht, damit die Woche später noch etwas erzählt.',
    types: {
      morgen: 'Morgen',
      abend: 'Abend',
      frei: 'Frei',
    },
    mood: 'Stimmung',
    energy: 'Energie',
    moodLegend: ['mies', 'flau', 'geht so', 'gut', 'richtig gut'] as const,
    energyLegend: ['leer', 'wenig', 'mittel', 'viel', 'randvoll'] as const,
    sleepLegend: ['schlecht', 'unruhig', 'okay', 'gut', 'tief'] as const,
    gratitude: 'Wofür bist du dankbar?',
    gratitudeHint: 'Drei Kleinigkeiten reichen. Je konkreter, desto besser.',
    sleepTitle: 'Schlaf letzte Nacht',
    bedtime: 'Eingeschlafen',
    wakeTime: 'Aufgewacht',
    sleepHours: 'Schlafdauer',
    sleepHoursManual: 'oder Dauer (Stunden)',
    sleepQuality: 'Schlafqualität',
    bodyLabel: {
      morgen: 'Was macht den Tag heute gut?',
      abend: 'Sonst noch etwas zum Tag?',
      frei: 'Eintrag',
    },
    bodyPlaceholder: {
      morgen: 'Eine Sache, die heute zählt …',
      abend: 'Was ist hängen geblieben?',
      frei: 'Schreib einfach los.',
    },
    autosaveHint: 'Wird automatisch gespeichert.',
    emptyHint: 'Noch nichts eingetragen — was du tippst, speichert sich von selbst.',
    trend: 'Letzte 30 Tage',
    entries: 'Einträge',
    search: 'Im Journal suchen',
    noMatches: 'Nichts gefunden.',
    empty: 'Noch keine Einträge. Der erste ist der schwerste.',
  },

  review: {
    subtitle: (tag: string) => `Fünf Minuten am ${tag}. Der Spiegel oben schreibt sich von selbst — die vier Fragen sind der eigentliche Teil.`,
    mirror: 'Die Woche in Zahlen',
    tasksDone: 'Erledigt',
    tasksOpen: 'Liegen geblieben',
    plannedTime: 'Verplante Zeit',
    habitRate: 'Gewohnheiten',
    goalsHeading: 'Ziele in dieser Woche',
    nothingPlanned: 'Diese Woche war nichts im Kalender.',
    questions: {
      gelaufen: 'Was lief diese Woche gut?',
      gehakt: 'Woran hat es gehakt?',
      gelernt: 'Was habe ich gelernt?',
      naechste_woche: 'Was nehme ich mir für nächste Woche vor?',
    },
    thisWeek: 'Diese Woche',
    done: 'Zurückgeblickt',
    due: 'Rückblick steht aus',
    autosaveHint: 'Wird automatisch gespeichert.',
    dashboardNudge: (kw: string) => `Rückblick für ${kw} steht aus`,
  },

  resources: {
    title: 'Notizen & Gedanken',
    subtitle: 'Eigene Notizen und gemerkte Gedanken – alles, was noch kein Zuhause hat.',
    /** Knopf auf der Uebersicht und in der Navigation (V-12): legt eine Notiz an. */
    neu: 'Neue Notiz',
    archived: 'Archiviert',
    tags: 'Schlagwörter',
    tagsPlaceholder: 'studium, klausur',
    pin: 'Anheften',
    unpin: 'Lösen',
    markRead: 'Als gelesen markieren',
    markUnread: 'Als ungelesen markieren',
    openLink: 'Link öffnen',
    deleteTitle: 'Eintrag löschen?',
    deleteBody: 'Der Eintrag kommt samt angehängter Dateien für 30 Tage in den Papierkorb.',
    attachments: 'Dateien',
    addFile: 'Datei anhängen',
    download: 'Herunterladen',
    tooLarge: (max: string) => `Datei ist zu groß. Höchstens ${max} pro Datei.`,
    storageUsed: (groesse: string) => `${groesse} in Dateien`,
    storageWarn:
      'Deine Anhänge machen die Sicherungsdatei spürbar groß. Alte Dateien löschen hilft.',
    storageCritical:
      'Die Sicherungsdatei wird sehr groß und könnte beim Export scheitern. Räum hier auf.',
  },

  /**
   * Die Inbox (F-48, Entscheidung 1: Mischform): der gemeinsame Eingang unter
   * Aufgaben - neue Aufgaben ohne Einordnung und gemerkte Gedanken.
   */
  inbox: {
    title: 'Inbox',
    leer: 'Die Inbox ist leer. Alles verarbeitet.',
    aufgabenLeer: 'Keine Aufgaben in der Inbox.',
    gedanken: 'Gemerkte Gedanken',
    gedankenHinweis: 'Aus Braindump, Listen und Wissen. Derselbe Eintrag wie im Journal – Änderungen erscheinen überall.',
    /** Im Braindump: wohin ein gemerkter Gedanke geht. */
    braindumpHinweis: 'Ideen, Einfälle, Gedanken – einer pro Zeile. Was du für später merkst, steht auch in der',
    /** Link aus Journal und Listen in die Inbox. */
    ansehen: 'In der Inbox ansehen',
    auchInInbox: 'Steht auch in der Inbox',
    /** Bei aktivem Bereichsfilter in der Kopfzeile. */
    gefiltert: 'Nach Bereich gefiltert – Aufgaben und Gedanken ohne passenden Bereich sind ausgeblendet.',
  },

  wissen: {
    title: 'Wissen',
    add: 'Eintrag',
    empty: 'Noch nichts eingetragen – Bücher, Videos, Podcasts, Artikel, Kurse …',
    deleteTitle: 'Eintrag löschen?',
    deleteBody: 'Der Eintrag kommt mit Notizen und Erkenntnissen für 30 Tage in den Papierkorb.',
  },

  projects: {
    title: 'Projekte',
    subtitle: 'Alles, was mehr als einen Schritt braucht und ein Ende hat.',
    add: 'Projekt',
    edit: 'Projekt bearbeiten',
    create: 'Neues Projekt',
    empty: 'Noch keine Projekte. Alles, wofür du mehr als eine Aufgabe brauchst, ist eins.',
    description: 'Worum geht es?',
    descriptionPlaceholder: 'Ein, zwei Sätze — was am Ende fertig sein soll.',
    titlePlaceholder: 'z. B. Hausarbeit Biologie',
    goal: 'Zahlt ein auf',
    noGoal: 'Kein Ziel',
    addTask: 'Nächster Schritt …',
    noTasks: 'Noch keine Aufgaben.',
    progress: (done: number, total: number) => `${done} von ${total} erledigt`,
    stuck: 'Kein nächster Schritt',
    stuckHint:
      'Dieses Projekt hat keine Aufgabe, an der du gerade weiterarbeiten könntest. Das ist die häufigste Art, wie Projekte liegen bleiben.',
    statuses: {
      aktiv: 'Aktiv',
      pausiert: 'Pausiert',
      irgendwann: 'Irgendwann',
      abgeschlossen: 'Abgeschlossen',
    },
    deleteTitle: 'Projekt löschen?',
    deleteBody:
      'Das Projekt kommt für 30 Tage in den Papierkorb. Seine Aufgaben bleiben erhalten, nur ohne Projekt.',
  },

  papierkorb: {
    title: 'Papierkorb',
    subtitle: 'Gelöschtes bleibt 30 Tage hier, danach ist es endgültig weg.',
    /** Fuer Loeschfragen: statt „endgueltig“. */
    hinweis: 'Kommt für 30 Tage in den Papierkorb.',
    // Rueckgaengig-Meldung (Schritt 8)
    ohneTitel: 'Eintrag',
    meldung: {
      geloescht: (t: string) => `„${t}“ gelöscht`,
      entfernt: (t: string) => `„${t}“ entfernt`,
      erledigt: (t: string) => `Erledigt: „${t}“`,
      wieder_offen: (t: string) => `Wieder offen: „${t}“`,
      umgewandelt: (t: string) => `Umgewandelt: „${t}“`,
      verschoben: (t: string) => `Verschoben: „${t}“`,
      archiviert: (t: string) => `Archiviert: „${t}“`,
    },
    mehrereGeloescht: (n: number) => `${n} Einträge gelöscht`,
    rueckgaengig: 'Rückgängig',
    rueckgaengigTitel: 'Rückgängig (Strg+Z)',
    meldungSchliessen: 'Meldung schließen',
    wiederDa: (t: string) => `„${t}“ ist wieder da`,
    schonDa: (t: string) => `„${t}“ war schon wieder da`,
    zurueckGenommen: (t: string) => `Rückgängig gemacht: „${t}“`,
    /** Eine dabei angelegte Zeile wurde inzwischen bearbeitet und bleibt. */
    zurueckGenommenGeaendert: (t: string) => `Rückgängig gemacht – „${t}“ war inzwischen geändert und bleibt`,
    gehtNichtFehlt: (t: string) => `Geht nicht mehr – „${t}“ fehlt inzwischen`,
    nurAufAnderemGeraet: (n: number) => `Wiederhergestellt – ${n === 1 ? '1 Anhang liegt' : `${n} Anhänge liegen`} nur auf dem anderen Gerät`,
    teilweise: 'Wiederhergestellt – ein Rest bleibt im Papierkorb',
    gehtNicht: 'Geht nicht mehr – schon geändert oder endgültig gelöscht',
    unvollstaendig: 'Wird noch abgeglichen …',
    // Seite (Schritt 9)
    leer: 'Der Papierkorb ist leer.',
    leerHinweis: 'Was du löschst, landet hier und lässt sich 30 Tage lang zurückholen.',
    heute: 'Heute',
    gestern: 'Gestern',
    geloescht: (vor: string) => `gelöscht ${vor}`,
    /** Art eines entfernten Teil-Eintrags, z. B. „aus Wissen“. */
    entferntAus: (name: string) => `aus ${name}`,
    aufGeraet: (g: string) => `auf „${g}“`,
    restTage: (n: number) => (n === 0 ? 'läuft heute ab' : n === 1 ? 'noch 1 Tag' : `noch ${n} Tage`),
    wiederherstellen: 'Wiederherstellen',
    endgueltigLoeschen: 'Endgültig löschen',
    /** Am Handy sichtbar statt „Endgültig löschen“ (passt neben „Wiederherstellen“); der Name fuer Screenreader bleibt lang. */
    loeschenKurz: 'Löschen',
    endgueltigFrage: 'Endgültig löschen?',
    endgueltigText: (t: string) => `„${t}“ ist danach weg. Das lässt sich nicht rückgängig machen.`,
    leeren: 'Papierkorb leeren',
    leerenFrage: 'Papierkorb leeren?',
    leerenText: (n: number) => `${n === 1 ? '1 Eintrag wird' : `${n} Einträge werden`} endgültig gelöscht. Das lässt sich nicht rückgängig machen.`,
    nichtInSicherung: 'Nicht in der Sicherungsdatei: Der Papierkorb wird nicht exportiert, und das Einspielen einer Sicherung leert ihn.',
    mitSync: 'Mit Synchronisierung gilt er auf allen Geräten – auf dem Laptop Gelöschtes lässt sich auf dem Handy zurückholen.',
    ueberblick: 'Überblick',
    ueberblickFrage: 'Was liegt im Papierkorb, und was läuft bald ab?',
    eintraege: 'Einträge',
    naechsterAblauf: 'Nächster Ablauf',
    keinAblauf: '–',
    inTagen: (n: number) => (n === 0 ? 'heute' : n === 1 ? 'morgen' : `in ${n} Tagen`),
    spalteArt: 'Art',
    spalteAnzahl: 'Anzahl',
    spalteAblauf: 'Läuft ab',
    // Nebenwirkungen: „4 Aufgaben werden wieder angehängt“
    wiederAngehaengt: (n: number, name: string) => `${n} ${name} ${n === 1 ? 'wird' : 'werden'} wieder angehängt`,
    kommtMit: (n: number, name: string) => `${n} ${name} ${n === 1 ? 'kommt' : 'kommen'} mit zurück`,
    wirdEntfernt: (n: number, name: string) => `${n} ${name} ${n === 1 ? 'wird' : 'werden'} wieder entfernt`,
    /** Einzahl und Mehrzahl fuer die Nebenwirkungen; sonst „× Tabellenname“. */
    mehrzahl: {
      tasks: ['Aufgabe', 'Aufgaben'], events: ['Termin', 'Termine'], projects: ['Projekt', 'Projekte'], goals: ['Ziel', 'Ziele'],
      resources: ['Notiz', 'Notizen'], thoughts: ['Gedanke', 'Gedanken'], books: ['Wissenseintrag', 'Wissenseinträge'],
      focusGoals: ['Fokusziel', 'Fokusziele'], attachments: ['Anhang', 'Anhänge'], habits: ['Gewohnheit', 'Gewohnheiten'],
      habitEntries: ['Gewohnheitstag', 'Gewohnheitstage'], terminBestaetigungen: ['Terminbestätigung', 'Terminbestätigungen'],
      listenEintraege: ['Listeneintrag', 'Listeneinträge'], planEinheiten: ['Trainingseinheit', 'Trainingseinheiten'],
      trainingsplaene: ['Planversion', 'Planversionen'], reflexionsFragen: ['Frage', 'Fragen'], journalCategories: ['Bewertung', 'Bewertungen'],
      listen: ['Liste', 'Listen'], areas: ['Bereich', 'Bereiche'], categories: ['Kategorie', 'Kategorien'],
    } as Record<string, readonly [string, string]>,
    hinweise: {
      schon_da: (t: string) => `„${t}“ gibt es schon neu – bleibt hier.`,
      eltern_fehlen: (t: string) => `„${t}“ braucht etwas, das fehlt – erst das wiederherstellen.`,
      fehlt: (t: string) => `„${t}“ fehlt gerade – erst das wiederherstellen.`,
      datei_fehlt: (t: string) => `Die Datei zu „${t}“ liegt nur auf dem Gerät, auf dem gelöscht wurde.`,
      geaendert: (t: string) => `„${t}“ wurde inzwischen geändert und bleibt, wie es ist.`,
    },
    /** Das fehlende Elternteil liegt im Papierkorb - dann mit Namen. */
    elternFehlenMit: (t: string, eltern: string) => `„${t}“ braucht „${eltern}“ – erst das wiederherstellen.`,
    imPapierkorb: (n: number) => (n === 0 ? 'Der Papierkorb ist leer.' : `${n === 1 ? '1 Eintrag liegt' : `${n} Einträge liegen`} im Papierkorb.`),
    zumPapierkorb: 'Zum Papierkorb',
  },

  common: {
    save: 'Speichern',
    cancel: 'Abbrechen',
    delete: 'Löschen',
    close: 'Schließen',
    add: 'Hinzufügen',
    edit: 'Bearbeiten',
    back: 'Zurück',
    today: 'Heute',
    loading: 'Lädt …',
  },
} as const;
